/**
 * Floating cart icon and cart drawer for portal (Story 7-4).
 * Shows item count; tap opens drawer with list, +/- remove, total, Place Order.
 * Place Order → confirmation (delivery address, notes) → Confirm Order → success.
 */
import { useEffect, useState, useCallback } from "react";
import {
  ShoppingCartIcon,
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { usePortalCartStore, type CartLine } from "../../store/portalCartStore";
import { ProductPhoto } from "../catalog/ProductPhoto";
import { Button } from "../atoms/Button";
import {
  createPortalOrder,
  getPortalMe,
  type PortalOrderResponse,
  type StockWarningItem,
} from "../../services/portalOrderService";
import { formatCurrency } from "../../utils/currency";
import { formatDecimal } from "../../utils/numberFormat";
import { formatUnit } from "../../utils/portalProduct";
import { getApiErrorMessage } from "../../utils/apiError";
import { getGoogleMapsUrl } from "../../utils/maps";

type Step = "cart" | "confirm" | "success";

/** Read-only quantity shown in cart + confirm bullets (units or pack count). */
function cartLineDisplayedQuantity(line: CartLine): {
  displayedQty: number;
  qtyMode: "units" | "packs";
} {
  const packSizes = line.product.sales_pack_sizes ?? [];
  const hasPackSizes = packSizes.length > 0;
  const qtyMode = hasPackSizes ? (line.qty_mode ?? "units") : "units";
  const selectedPackSize = line.selected_pack_size ?? packSizes[0] ?? 1;
  const displayedQty =
    qtyMode === "packs"
      ? Math.max(1, Math.floor(line.quantity / Math.max(1, selectedPackSize)))
      : line.quantity;
  return { displayedQty, qtyMode };
}

export function PortalCartFloating() {
  const lines = usePortalCartStore((s) => s.lines);
  const updateQuantity = usePortalCartStore((s) => s.updateQuantity);
  const setLineQtyMode = usePortalCartStore((s) => s.setLineQtyMode);
  const setLinePackSize = usePortalCartStore((s) => s.setLinePackSize);
  const removeItem = usePortalCartStore((s) => s.removeItem);
  const clearCart = usePortalCartStore((s) => s.clearCart);
  const hydrateFromStorage = usePortalCartStore((s) => s.hydrateFromStorage);
  const itemCount = usePortalCartStore((s) => s.itemCount);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [step, setStep] = useState<Step>("cart");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryLatitude, setDeliveryLatitude] = useState<number | null>(null);
  const [deliveryLongitude, setDeliveryLongitude] = useState<number | null>(null);
  const [notes, setNotes] = useState("");
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orderResult, setOrderResult] = useState<PortalOrderResponse | null>(null);
  const [stockWarnings, setStockWarnings] = useState<StockWarningItem[] | null>(null);
  const [restoredFromStorage, setRestoredFromStorage] = useState(false);

  useEffect(() => {
    hydrateFromStorage();
    const raw = localStorage.getItem("portal_cart");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) setRestoredFromStorage(true);
      } catch {
        // ignore
      }
    }
  }, [hydrateFromStorage]);

  const openDrawer = useCallback(() => {
    setDrawerOpen(true);
    setStep("cart");
    setError(null);
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setStep("cart");
    setError(null);
    setOrderResult(null);
    setStockWarnings(null);
  }, []);

  const goToConfirm = useCallback(async () => {
    setError(null);
    try {
      const me = await getPortalMe();
      setDeliveryAddress(me.delivery_address ?? "");
      setDeliveryLatitude(me.delivery_latitude ?? null);
      setDeliveryLongitude(me.delivery_longitude ?? null);
      setNotes("");
      setCsrfToken(me.csrf_token ?? null);
      setStep("confirm");
    } catch (e) {
      setError(getApiErrorMessage(e, "Could not load delivery address"));
    }
  }, []);

  const submitOrder = useCallback(async () => {
    if (lines.length === 0) return;
    setLoading(true);
    setError(null);
    try {
      const payload = {
        items: lines.map((l) => ({ product_id: l.product.id, quantity: l.quantity })),
        delivery_address: deliveryAddress.trim() || undefined,
        delivery_latitude:
          deliveryLatitude != null && deliveryLongitude != null && !Number.isNaN(deliveryLatitude) && !Number.isNaN(deliveryLongitude)
            ? deliveryLatitude
            : undefined,
        delivery_longitude:
          deliveryLatitude != null && deliveryLongitude != null && !Number.isNaN(deliveryLatitude) && !Number.isNaN(deliveryLongitude)
            ? deliveryLongitude
            : undefined,
        notes: notes.trim() || undefined,
      };
      const result = await createPortalOrder(payload, csrfToken);
      setOrderResult(result);
      setStockWarnings(result.stock_warnings ?? null);
      clearCart();
      setError(null);
      setStep("success");
    } catch (e) {
      setError(getApiErrorMessage(e, "Failed to place order"));
    } finally {
      setLoading(false);
    }
  }, [lines, deliveryAddress, deliveryLatitude, deliveryLongitude, notes, csrfToken, clearCart]);

  const orderTotal = lines.reduce((sum, l) => sum + l.quantity * Number(l.product.unit_price), 0);

  const count = itemCount();
  const showFloatingButton = true; // always show for customer; count can be 0

  return (
    <>
      {/* Floating cart button */}
      {showFloatingButton && (
        <button
          type="button"
          onClick={openDrawer}
          className="fixed bottom-fab-offset end-4 md:bottom-6 md:end-6 z-40 flex items-center justify-center w-14 h-14 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800"
          aria-label={`Cart, ${formatDecimal(count)} items`}
        >
          <ShoppingCartIcon className="w-7 h-7" />
          {count > 0 && (
            <span className="absolute -top-1 -end-1 flex h-5 w-5 items-center justify-center rounded-full bg-danger-500 text-xs font-bold text-white">
              {count > 99 ? "99+" : formatDecimal(count)}
            </span>
          )}
        </button>
      )}

      {/* Cart / Confirm / Success drawer */}
      {drawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 md:bg-transparent"
            aria-hidden
            onClick={closeDrawer}
          />
          <aside
            className="fixed end-0 top-0 bottom-0 w-full max-w-md bg-white dark:bg-neutral-800 shadow-xl z-50 flex flex-col"
            role="dialog"
            aria-label="Cart"
          >
            <div className="flex items-center justify-between h-14 px-4 border-b border-neutral-200 dark:border-neutral-700">
              <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
                {step === "cart" && "Your order"}
                {step === "confirm" && "Confirm order"}
                {step === "success" && "Order placed"}
              </h2>
              <button
                type="button"
                onClick={closeDrawer}
                className="p-2 rounded-lg text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                aria-label="Close"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {step === "cart" && (
                <CartView
                  lines={lines}
                  orderTotal={orderTotal}
                  onUpdateQuantity={updateQuantity}
                  onSetQtyMode={setLineQtyMode}
                  onSetPackSize={setLinePackSize}
                  onRemove={removeItem}
                  onPlaceOrder={goToConfirm}
                  restoredFromStorage={restoredFromStorage}
                />
              )}
              {step === "confirm" && (
                <ConfirmView
                  lines={lines}
                  orderTotal={orderTotal}
                  deliveryAddress={deliveryAddress}
                  setDeliveryAddress={setDeliveryAddress}
                  deliveryLatitude={deliveryLatitude}
                  setDeliveryLatitude={setDeliveryLatitude}
                  deliveryLongitude={deliveryLongitude}
                  setDeliveryLongitude={setDeliveryLongitude}
                  notes={notes}
                  setNotes={setNotes}
                  onSubmit={submitOrder}
                  onBack={() => setStep("cart")}
                  loading={loading}
                  error={error}
                />
              )}
              {step === "success" && orderResult && (
                <SuccessView
                  orderNumber={orderResult.order_number}
                  stockWarnings={stockWarnings}
                  deliveryAddress={orderResult.delivery_address}
                  deliveryLatitude={
                    typeof orderResult.delivery_latitude === "number"
                      ? orderResult.delivery_latitude
                      : orderResult.delivery_latitude != null
                        ? Number(orderResult.delivery_latitude)
                        : null
                  }
                  deliveryLongitude={
                    typeof orderResult.delivery_longitude === "number"
                      ? orderResult.delivery_longitude
                      : orderResult.delivery_longitude != null
                        ? Number(orderResult.delivery_longitude)
                        : null
                  }
                  onClose={closeDrawer}
                />
              )}
            </div>
          </aside>
        </>
      )}
    </>
  );
}

function CartView({
  lines,
  orderTotal,
  onUpdateQuantity,
  onSetQtyMode,
  onSetPackSize,
  onRemove,
  onPlaceOrder,
  restoredFromStorage,
}: {
  lines: CartLine[];
  orderTotal: number;
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onSetQtyMode: (productId: string, mode: "units" | "packs") => void;
  onSetPackSize: (productId: string, packSize: number) => void;
  onRemove: (productId: string) => void;
  onPlaceOrder: () => void;
  restoredFromStorage?: boolean;
}) {
  if (lines.length === 0) {
    return (
      <p className="text-neutral-600 dark:text-neutral-400 py-8 text-center">
        Your cart is empty. Add products from the catalog.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      {restoredFromStorage && (
        <p className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
          Cart restored. Prices shown may have changed; totals are confirmed at checkout.
        </p>
      )}
      <ul className="space-y-3">
        {lines.map((line) => {
          const lineTotal = line.quantity * Number(line.product.unit_price);
          const packSizes = line.product.sales_pack_sizes ?? [];
          const hasPackSizes = packSizes.length > 0;
          const { displayedQty, qtyMode } = cartLineDisplayedQuantity(line);
          const selectedPackSize = line.selected_pack_size ?? packSizes[0] ?? 1;
          return (
            <li
              key={line.product.id}
              className="flex flex-col gap-2 p-3 rounded-lg border border-neutral-200 dark:border-neutral-700"
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2 min-w-0">
                  <ProductPhoto attachmentId={line.product.primary_photo_id} size="sm" />
                  <span className="font-medium text-neutral-900 dark:text-neutral-100 truncate">
                    {line.product.name}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(line.product.id)}
                  className="p-1 text-neutral-500 hover:text-danger-600"
                  aria-label={`Remove ${line.product.name}`}
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-neutral-500 dark:text-neutral-400">
                  {formatCurrency(Number(line.product.unit_price))} {formatUnit(line.product.unit)}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      onUpdateQuantity(
                        line.product.id,
                        qtyMode === "packs"
                          ? (displayedQty - 1) * Math.max(1, selectedPackSize)
                          : line.quantity - 1
                      )
                    }
                    className="p-1.5 rounded border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    aria-label="Decrease quantity"
                  >
                    <MinusIcon className="w-4 h-4" />
                  </button>
                  <span className="min-w-[2rem] text-center font-medium">
                    {formatDecimal(displayedQty)}
                    {qtyMode === "packs" ? " pack" : ""}
                    {qtyMode === "packs" && displayedQty !== 1 ? "s" : ""}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      onUpdateQuantity(
                        line.product.id,
                        qtyMode === "packs"
                          ? (displayedQty + 1) * Math.max(1, selectedPackSize)
                          : line.quantity + 1
                      )
                    }
                    className="p-1.5 rounded border border-neutral-300 dark:border-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    aria-label="Increase quantity"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {hasPackSizes && (
                <div className="space-y-2">
                  <div className="inline-flex rounded-lg border border-neutral-300 dark:border-neutral-600 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onSetQtyMode(line.product.id, "units")}
                      className={`px-2 py-1 text-xs ${
                        qtyMode === "units"
                          ? "bg-primary-600 text-white"
                          : "bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
                      }`}
                    >
                      Units
                    </button>
                    <button
                      type="button"
                      onClick={() => onSetQtyMode(line.product.id, "packs")}
                      className={`px-2 py-1 text-xs border-s border-neutral-300 dark:border-neutral-600 ${
                        qtyMode === "packs"
                          ? "bg-primary-600 text-white"
                          : "bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300"
                      }`}
                    >
                      Packs
                    </button>
                  </div>
                  {qtyMode === "packs" && (
                    <div className="flex items-center gap-2 flex-wrap">
                      {packSizes.map((size) => (
                        <button
                          key={size}
                          type="button"
                          onClick={() => onSetPackSize(line.product.id, size)}
                          className={`px-2 py-1 rounded-md text-xs border ${
                            selectedPackSize === size
                              ? "bg-primary-600 text-white border-primary-600"
                              : "border-neutral-300 dark:border-neutral-600 text-neutral-700 dark:text-neutral-300"
                          }`}
                        >
                          {formatDecimal(size)}/pack
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="text-right font-medium text-primary-600 dark:text-primary-400">
                Line total: {formatCurrency(lineTotal)}
              </div>
            </li>
          );
        })}
      </ul>
      <div className="border-t border-neutral-200 dark:border-neutral-700 pt-4">
        <p className="flex justify-between text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          <span>Order total</span>
          <span>{formatCurrency(orderTotal)}</span>
        </p>
      </div>
      <Button variant="primary" className="w-full min-h-[44px]" onClick={onPlaceOrder}>
        Place Order
      </Button>
    </div>
  );
}

function ConfirmView({
  lines,
  orderTotal,
  deliveryAddress,
  setDeliveryAddress,
  deliveryLatitude,
  setDeliveryLatitude,
  deliveryLongitude,
  setDeliveryLongitude,
  notes,
  setNotes,
  onSubmit,
  onBack,
  loading,
  error,
}: {
  lines: CartLine[];
  orderTotal: number;
  deliveryAddress: string;
  setDeliveryAddress: (v: string) => void;
  deliveryLatitude: number | null;
  setDeliveryLatitude: (v: number | null) => void;
  deliveryLongitude: number | null;
  setDeliveryLongitude: (v: number | null) => void;
  notes: string;
  setNotes: (v: string) => void;
  onSubmit: () => void;
  onBack: () => void;
  loading: boolean;
  error: string | null;
}) {
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setDeliveryLatitude(pos.coords.latitude);
        setDeliveryLongitude(pos.coords.longitude);
      },
      () => {
        // User denied or error; leave coords as-is
      }
    );
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-neutral-600 dark:text-neutral-400">
        <p className="font-medium text-neutral-900 dark:text-neutral-100 mb-1">Items</p>
        <ul className="list-disc list-inside">
          {lines.map((l) => {
            const { displayedQty, qtyMode } = cartLineDisplayedQuantity(l);
            return (
              <li key={l.product.id}>
                {l.product.name} × {formatDecimal(displayedQty)}
                {qtyMode === "packs" ? " pack" : ""}
                {qtyMode === "packs" && displayedQty !== 1 ? "s" : ""} —{" "}
                {formatCurrency(l.quantity * Number(l.product.unit_price))}
              </li>
            );
          })}
        </ul>
        <p className="mt-2 font-medium">Total: {formatCurrency(orderTotal)}</p>
      </div>
      <div>
        <label
          htmlFor="delivery-address"
          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
        >
          Delivery address
        </label>
        <textarea
          id="delivery-address"
          rows={3}
          value={deliveryAddress}
          onChange={(e) => setDeliveryAddress(e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          placeholder="Enter delivery address"
        />
      </div>
      <div>
        <p className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          Delivery coordinates (optional)
        </p>
        <div className="flex flex-wrap gap-2 items-center">
          <input
            type="number"
            step="any"
            placeholder="Latitude"
            value={deliveryLatitude ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setDeliveryLatitude(v === "" ? null : Number(v));
            }}
            className="w-28 px-2 py-1.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm"
          />
          <input
            type="number"
            step="any"
            placeholder="Longitude"
            value={deliveryLongitude ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              setDeliveryLongitude(v === "" ? null : Number(v));
            }}
            className="w-28 px-2 py-1.5 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-sm"
          />
          <button
            type="button"
            onClick={handleUseMyLocation}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            Use my location
          </button>
        </div>
      </div>
      <div>
        <label
          htmlFor="order-notes"
          className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1"
        >
          Order notes (optional)
        </label>
        <textarea
          id="order-notes"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
          placeholder="Special instructions"
        />
      </div>
      {error && <p className="text-sm text-danger-600 dark:text-danger-400">{error}</p>}
      <div className="flex gap-3">
        <Button variant="secondary" className="flex-1 min-h-[44px]" onClick={onBack} type="button">
          Back
        </Button>
        <Button
          variant="primary"
          className="flex-1 min-h-[44px]"
          onClick={onSubmit}
          disabled={loading}
        >
          {loading ? "Placing order…" : "Confirm Order"}
        </Button>
      </div>
    </div>
  );
}

function SuccessView({
  orderNumber,
  stockWarnings,
  deliveryAddress,
  deliveryLatitude,
  deliveryLongitude,
  onClose,
}: {
  orderNumber: string;
  stockWarnings: StockWarningItem[] | null;
  deliveryAddress: string | null;
  deliveryLatitude: number | null;
  deliveryLongitude: number | null;
  onClose: () => void;
}) {
  const mapUrl =
    getGoogleMapsUrl({
      latitude: deliveryLatitude ?? undefined,
      longitude: deliveryLongitude ?? undefined,
      address: deliveryAddress ?? undefined,
    }) ?? null;

  return (
    <div className="space-y-4 text-center py-4">
      <p className="text-xl font-semibold text-green-600 dark:text-green-400">
        Order Placed Successfully!
      </p>
      <p className="text-neutral-700 dark:text-neutral-300">
        Order number: <strong>{orderNumber}</strong>
      </p>
      {mapUrl && (
        <p>
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary-600 dark:text-primary-400 hover:underline text-sm"
          >
            View delivery on map
          </a>
        </p>
      )}
      {stockWarnings && stockWarnings.length > 0 && (
        <div className="text-left rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm text-amber-800 dark:text-amber-200">
          <p className="font-medium mb-1">Stock notice</p>
          <ul className="list-disc list-inside">
            {stockWarnings.map((w) => (
              <li key={w.product_id}>
                {w.product_name}: requested {formatDecimal(w.requested)}, available{" "}
                {formatDecimal(w.available)} (backorder)
              </li>
            ))}
          </ul>
        </div>
      )}
      <Button variant="primary" className="w-full min-h-[44px]" onClick={onClose}>
        Done
      </Button>
    </div>
  );
}
